/* eslint-disable */
import { Recipient } from "../../../api/common/common";
import {
  SalesOrder_LogisticsCompany,
  salesOrder_LogisticsCompanyFromJSON,
  salesOrder_LogisticsCompanyToJSON,
  SalesOrder_PaymentType,
  salesOrder_PaymentTypeFromJSON,
  salesOrder_PaymentTypeToJSON,
  SalesOrder_ReceiptMethod,
  salesOrder_ReceiptMethodFromJSON,
  salesOrder_ReceiptMethodToJSON,
  SalesReturn_RefundChannel,
  salesReturn_RefundChannelFromJSON,
  salesReturn_RefundChannelToJSON,
  SalesReturn_ReturnChannel,
  salesReturn_ReturnChannelFromJSON,
  salesReturn_ReturnChannelToJSON,
} from "../../../api/orders/resources";
import { Timestamp } from "../../../google/protobuf/timestamp";

export const protobufPackage = "store.api";

export interface SalesOrder {
  /**
   * 訂單編號
   * ID that is visible in the UI for customers.
   */
  id: string;
  /** 訂單前端顯示狀態 */
  state: SalesOrder_FrState;
  /** 付款類型 */
  paymentType: SalesOrder_PaymentType;
  /** 下單時間/訂單成立時間 */
  createTime:
    | Date
    | undefined;
  /** 付款超時時間 */
  paymentRemindTimeout:
    | Date
    | undefined;
  /** 付款時間 */
  paymentTime:
    | Date
    | undefined;
  /** 預計出貨日 */
  expectedOutflowTime:
    | Date
    | undefined;
  /** 簽收時間 */
  claimedTime:
    | Date
    | undefined;
  /** 鑑賞日期, `claimed_time`+ n days. Valid only when 簽收時間 existed. */
  returnRemindTimeout:
    | Date
    | undefined;
  /** 門市最後取件日 */
  storeLastPickupTime:
    | Date
    | undefined;
  /** 訂單取消時間 */
  cancelTime:
    | Date
    | undefined;
  /** 退款完成時間 */
  refundCompletedTime:
    | Date
    | undefined;
  /** 配送物流 */
  logisticsCompany: SalesOrder_LogisticsCompany;
  /** 配送編號/物流編號 */
  shipmentTrackingNumber: string;
  /** 是否拋轉待出貨 */
  waitingForShipping: boolean;
  /** 是否未取貨 */
  noPickup: boolean;
  /** 是否折讓 */
  hasCreditNote: boolean;
  /** 是否為中獎發票 */
  hasWinnerReceipt: boolean;
  /** 客服非服務時間 */
  csOffline: boolean;
  /** 發票處理方式 */
  receiptMethod: SalesOrder_ReceiptMethod;
  /** 發票內容 */
  receiptContent: string;
  /** 收件人資訊 */
  recipientInfo:
    | Recipient
    | undefined;
  /** 已開立發票 */
  invoiceInfo:
    | SalesOrder_Invoice
    | undefined;
  /** 取消訂單退款流程 */
  refundIndicator:
    | SalesOrder_RefundIndicator
    | undefined;
  /** 原始訂單內容（不含退貨） */
  cartInfo:
    | SalesOrder_CartInfo
    | undefined;
  /** 訂單退貨後內容. Only return in `GetOrder` */
  finalCartInfo:
    | SalesOrder_CartInfo
    | undefined;
  /** UI buttons */
  buttons: SalesOrder_Buttons | undefined;
}

/** 前端顯示狀態 */
export enum SalesOrder_FrState {
  FR_STATE_UNSPECIFIED = 0,
  /** FR_STATE_WAIT_BUYER_PAY - 待付款/未完成付款, ref: payment_remind_timeout */
  FR_STATE_WAIT_BUYER_PAY = 1,
  /**
   * FR_STATE_NEW - 收到新訂單
   * 【 收到新訂單 】
   */
  FR_STATE_NEW = 2,
  /**
   * FR_STATE_HANDLING - 訂單處理中
   * 【 訂單處理中 】, ref: empty expected_outflow_time
   * 【 已確認訂單，預計 MM-DD 出貨 】, ref: expected_outflow_time
   * 【 包裹準備中，預計 MM-DD 出貨 】, ref: expected_outflow_time and waiting_for_shipping is true
   */
  FR_STATE_HANDLING = 3,
  /**
   * FR_STATE_SHIPPED - 已出貨
   * 【 包裝完成，預計 MM-DD 出貨 】
   */
  FR_STATE_SHIPPED = 4,
  /**
   * FR_STATE_IN_TRANSIT - 配送中/派送中
   * 【 包裹配送中 】
   */
  FR_STATE_IN_TRANSIT = 5,
  /**
   * FR_STATE_DELIVERED - 已送達
   * 【 鑑賞期至：YYYY-MM-DD 】
   */
  FR_STATE_DELIVERED = 6,
  /**
   * FR_STATE_DELIVERED_TO_STORE - 已送達門市-待取件
   * 【 請於 YYYY-MM-DD 前完成取件 】
   */
  FR_STATE_DELIVERED_TO_STORE = 7,
  /**
   * FR_STATE_OUTFLOW_ERROR - 訂單出貨異常
   * 【 包裹配送異常 】
   */
  FR_STATE_OUTFLOW_ERROR = 8,
  /**
   * FR_STATE_RECEIVED - 已完成取件
   * 【 YYYY-MM-DD 】
   */
  FR_STATE_RECEIVED = 9,
  /**
   * FR_STATE_CANCELED - 訂單已取消
   * 取消/退款進度 - 【 退款處理中 】, ref: has payment_time and empty refund_completed_time
   * 取消/退款進度 - 【 退款完成 】, ref: has payment_time and has refund_completed_time
   * 超商未取件取消訂單 - 【 YYYY-MM-DD HH:MM 】, ref: payment_type is store and no_pickup is true
   * 訂單已取消 - 【 未付款完成，於 YYYY-MM-DD HH:MM 取消 】, ref: payment_type is credit, linepay, applepay, oversea and total > 0 and empty payment_time
   * 訂單已取消 - 【 YYYY-MM-DD HH:MM 】
   */
  FR_STATE_CANCELED = 10,
}

export function salesOrder_FrStateFromJSON(object: any): SalesOrder_FrState {
  switch (object) {
    case 0:
    case "FR_STATE_UNSPECIFIED":
      return SalesOrder_FrState.FR_STATE_UNSPECIFIED;
    case 1:
    case "FR_STATE_WAIT_BUYER_PAY":
      return SalesOrder_FrState.FR_STATE_WAIT_BUYER_PAY;
    case 2:
    case "FR_STATE_NEW":
      return SalesOrder_FrState.FR_STATE_NEW;
    case 3:
    case "FR_STATE_HANDLING":
      return SalesOrder_FrState.FR_STATE_HANDLING;
    case 4:
    case "FR_STATE_SHIPPED":
      return SalesOrder_FrState.FR_STATE_SHIPPED;
    case 5:
    case "FR_STATE_IN_TRANSIT":
      return SalesOrder_FrState.FR_STATE_IN_TRANSIT;
    case 6:
    case "FR_STATE_DELIVERED":
      return SalesOrder_FrState.FR_STATE_DELIVERED;
    case 7:
    case "FR_STATE_DELIVERED_TO_STORE":
      return SalesOrder_FrState.FR_STATE_DELIVERED_TO_STORE;
    case 8:
    case "FR_STATE_OUTFLOW_ERROR":
      return SalesOrder_FrState.FR_STATE_OUTFLOW_ERROR;
    case 9:
    case "FR_STATE_RECEIVED":
      return SalesOrder_FrState.FR_STATE_RECEIVED;
    case 10:
    case "FR_STATE_CANCELED":
      return SalesOrder_FrState.FR_STATE_CANCELED;
    default:
      throw new tsProtoGlobalThis.Error("Unrecognized enum value " + object + " for enum SalesOrder_FrState");
  }
}

export function salesOrder_FrStateToJSON(object: SalesOrder_FrState): string {
  switch (object) {
    case SalesOrder_FrState.FR_STATE_UNSPECIFIED:
      return "FR_STATE_UNSPECIFIED";
    case SalesOrder_FrState.FR_STATE_WAIT_BUYER_PAY:
      return "FR_STATE_WAIT_BUYER_PAY";
    case SalesOrder_FrState.FR_STATE_NEW:
      return "FR_STATE_NEW";
    case SalesOrder_FrState.FR_STATE_HANDLING:
      return "FR_STATE_HANDLING";
    case SalesOrder_FrState.FR_STATE_SHIPPED:
      return "FR_STATE_SHIPPED";
    case SalesOrder_FrState.FR_STATE_IN_TRANSIT:
      return "FR_STATE_IN_TRANSIT";
    case SalesOrder_FrState.FR_STATE_DELIVERED:
      return "FR_STATE_DELIVERED";
    case SalesOrder_FrState.FR_STATE_DELIVERED_TO_STORE:
      return "FR_STATE_DELIVERED_TO_STORE";
    case SalesOrder_FrState.FR_STATE_OUTFLOW_ERROR:
      return "FR_STATE_OUTFLOW_ERROR";
    case SalesOrder_FrState.FR_STATE_RECEIVED:
      return "FR_STATE_RECEIVED";
    case SalesOrder_FrState.FR_STATE_CANCELED:
      return "FR_STATE_CANCELED";
    default:
      throw new tsProtoGlobalThis.Error("Unrecognized enum value " + object + " for enum SalesOrder_FrState");
  }
}

/**
 * 訂單品項
 * Every item is uniquely identified by the `Item.id`.
 * When displayed as a collection, the items should be sorted by `sku_id` ASC.
 */
export interface SalesOrder_Item {
  /** Unique id generated by the server for this `Item`, not visible to customers */
  id: string;
  /** 商品編號 */
  skuId: string;
  /** 商品名稱 */
  productName: string;
  /** 商品圖片. Image 360 pixels. */
  productImage: string;
  /** 顏色 */
  colorName: string;
  /** 尺寸 */
  sizeName: string;
  /** 尺碼後綴 */
  sizeSuffix: string;
  /** 單價 */
  unitPrice: string;
  /** 折扣後單價 */
  discountedUnitPrice: string;
  /** 活動名稱 */
  promotionName: string;
  /** 活動攤提折扣 */
  promotionAmortizedDeduction: string;
  /** 購買數量 */
  quantity: number;
  /** 小計加總 */
  subtotal: string;
  /** 商品單價小計 */
  unitPriceSubtotal: string;
  /** 是否上架 */
  published: boolean;
}

/** 訂單內容 */
export interface SalesOrder_CartInfo {
  /** 訂單品項. Order by `sku_id` ASC */
  items: SalesOrder_Item[];
  /**
   * 數量加總/商品數量
   * Sum of all `Item.quantity`.
   */
  quantity: number;
  /**
   * 商品單價小計
   * Sum of all `Item.unit_price_subtotal`.
   */
  subtotal: string;
  /** 活動折抵金額 */
  promotionDeduction: string;
  /** 物流費/運費 */
  shipping: string;
  /** 虛帳 */
  balance: string;
  /** 合計/請款金額 = 小計加總 - 折抵金額 - 虛帳 + 物流費 */
  total: string;
  /** 是否已全部評價 */
  allItemsRated: boolean;
  /** 美金兌換台幣匯率 */
  exchangeRateUsdNtd: string;
  /** 合計/請款金額（美金） */
  totalUsd: string;
}

/** For UI buttons */
export interface SalesOrder_Buttons {
  /**
   * 在線客服
   * 聯繫客服(非服務時間), ref：cs_offline
   */
  csBtn: boolean;
  /** 取消訂單 */
  cancelTradeBtn: boolean;
  /** 付款 */
  payBtn: boolean;
  /** 查看物流 */
  logisticsBtn: boolean;
  /**
   * 評價
   * 查看評價, ref：all_items_rated
   */
  rateBtn: boolean;
  /** 刪除訂單 */
  deleteTradeBtn: boolean;
  /** 再次購買 */
  buyAgainBtn: boolean;
  /** 申請退貨 */
  applyReturnBtn: boolean;
  /** 退貨詳情 */
  returnInfoBtn: boolean;
  /**
   * 下載發票
   * 中獎發票, ref：has_winner_receipt
   * 下載折讓, ref: has_credit_note
   */
  downloadReceiptBtn: boolean;
}

/** 物流歷程 */
export interface SalesOrder_LogisticsRoute {
  /** 類型 */
  type: SalesOrder_LogisticsRoute_Type;
  /** 發生時間 */
  timestamp:
    | Date
    | undefined;
  /** 物流配送查詢連結 */
  url: string;
  /** 預計出貨時間, required when type is CONFIRM_ORDER */
  expectedOutflowTime:
    | Date
    | undefined;
  /** 門市到店日, required when type is DELIVERED_TO_STORE */
  storeDeliveredTime:
    | Date
    | undefined;
  /** 門市最後取件日, required when type is DELIVERED_TO_STORE */
  storeLastPickupTime:
    | Date
    | undefined;
  /** 路由資訊 */
  desc: string;
}

export enum SalesOrder_LogisticsRoute_Type {
  /** NEW - 收到新訂單 */
  NEW = 0,
  /** HANDLING - 訂單處理中 */
  HANDLING = 1,
  /** CONFIRMED - 已確認訂單 */
  CONFIRMED = 2,
  /** PACKAGE_PREPARING - 包裹準備中 */
  PACKAGE_PREPARING = 3,
  /** PACKAGE_COMPLETED - 已裝車出庫 */
  PACKAGE_COMPLETED = 4,
  /** IN_TRANSIT - 包裹配送中 */
  IN_TRANSIT = 5,
  /** DELIVERED_TO_STORE - 已送達門市 */
  DELIVERED_TO_STORE = 6,
  /** PACKAGE_RECEIVED - 已取件 */
  PACKAGE_RECEIVED = 7,
  /** STORE_RETURN - 門市退回 */
  STORE_RETURN = 8,
  /** PACKAGE_NO_PICKUP_RETURN - 包裹未取退回 */
  PACKAGE_NO_PICKUP_RETURN = 9,
  /** PACKAGE_SIGNED - 已簽收 */
  PACKAGE_SIGNED = 10,
}

export function salesOrder_LogisticsRoute_TypeFromJSON(object: any): SalesOrder_LogisticsRoute_Type {
  switch (object) {
    case 0:
    case "NEW":
      return SalesOrder_LogisticsRoute_Type.NEW;
    case 1:
    case "HANDLING":
      return SalesOrder_LogisticsRoute_Type.HANDLING;
    case 2:
    case "CONFIRMED":
      return SalesOrder_LogisticsRoute_Type.CONFIRMED;
    case 3:
    case "PACKAGE_PREPARING":
      return SalesOrder_LogisticsRoute_Type.PACKAGE_PREPARING;
    case 4:
    case "PACKAGE_COMPLETED":
      return SalesOrder_LogisticsRoute_Type.PACKAGE_COMPLETED;
    case 5:
    case "IN_TRANSIT":
      return SalesOrder_LogisticsRoute_Type.IN_TRANSIT;
    case 6:
    case "DELIVERED_TO_STORE":
      return SalesOrder_LogisticsRoute_Type.DELIVERED_TO_STORE;
    case 7:
    case "PACKAGE_RECEIVED":
      return SalesOrder_LogisticsRoute_Type.PACKAGE_RECEIVED;
    case 8:
    case "STORE_RETURN":
      return SalesOrder_LogisticsRoute_Type.STORE_RETURN;
    case 9:
    case "PACKAGE_NO_PICKUP_RETURN":
      return SalesOrder_LogisticsRoute_Type.PACKAGE_NO_PICKUP_RETURN;
    case 10:
    case "PACKAGE_SIGNED":
      return SalesOrder_LogisticsRoute_Type.PACKAGE_SIGNED;
    default:
      throw new tsProtoGlobalThis.Error(
        "Unrecognized enum value " + object + " for enum SalesOrder_LogisticsRoute_Type",
      );
  }
}

export function salesOrder_LogisticsRoute_TypeToJSON(object: SalesOrder_LogisticsRoute_Type): string {
  switch (object) {
    case SalesOrder_LogisticsRoute_Type.NEW:
      return "NEW";
    case SalesOrder_LogisticsRoute_Type.HANDLING:
      return "HANDLING";
    case SalesOrder_LogisticsRoute_Type.CONFIRMED:
      return "CONFIRMED";
    case SalesOrder_LogisticsRoute_Type.PACKAGE_PREPARING:
      return "PACKAGE_PREPARING";
    case SalesOrder_LogisticsRoute_Type.PACKAGE_COMPLETED:
      return "PACKAGE_COMPLETED";
    case SalesOrder_LogisticsRoute_Type.IN_TRANSIT:
      return "IN_TRANSIT";
    case SalesOrder_LogisticsRoute_Type.DELIVERED_TO_STORE:
      return "DELIVERED_TO_STORE";
    case SalesOrder_LogisticsRoute_Type.PACKAGE_RECEIVED:
      return "PACKAGE_RECEIVED";
    case SalesOrder_LogisticsRoute_Type.STORE_RETURN:
      return "STORE_RETURN";
    case SalesOrder_LogisticsRoute_Type.PACKAGE_NO_PICKUP_RETURN:
      return "PACKAGE_NO_PICKUP_RETURN";
    case SalesOrder_LogisticsRoute_Type.PACKAGE_SIGNED:
      return "PACKAGE_SIGNED";
    default:
      throw new tsProtoGlobalThis.Error(
        "Unrecognized enum value " + object + " for enum SalesOrder_LogisticsRoute_Type",
      );
  }
}

/** 發票內容 */
export interface SalesOrder_Invoice {
  /** 年份（民國） */
  rocYear: string;
  /** 期別, ex 01-02, 03-04 等 */
  period: string;
  /** 發票號碼, ex AA-1234567 */
  invoiceNo: string;
  /** 發票開立時間, yyyy-MM-dd hh:mm:ss */
  invoiceTime: string;
  /** 三聯發票專用，格式25 */
  triplicateCode: string;
  /** 隨機碼 */
  randomCode: string;
  /** 總計 */
  amount: string;
  /** 賣方統編 */
  sellerTaxNo: string;
  /** 買方統編 */
  buyerTaxNo: string;
  /** 發票號碼及資訊 barcode, ex: {所屬雙數月 ex:02}{發票號碼AA12345678}{隨機碼} */
  barcode: string;
  /** 明細 qrcode 左 */
  leftQrcode: string;
  /** 明細 qrcode 右 */
  rightQrcode: string;
  /**
   * 交易明細
   * 銷售額
   */
  amountBeforeTax: string;
  /** 稅別 */
  taxCategory: string;
  /** 稅額 */
  taxAmount: string;
  /** 備註 */
  memo: string;
  /** 是否作廢 */
  isCancel: boolean;
}

/** 取消訂單退款流程 */
export interface SalesOrder_RefundIndicator {
  /** 取消訂單申請退款 */
  new:
    | SalesOrder_RefundIndicator_Item
    | undefined;
  /** 退款處理中 */
  handling:
    | SalesOrder_RefundIndicator_Item
    | undefined;
  /** 虛帳退款處理完成待退刷 */
  pendingRefund:
    | SalesOrder_RefundIndicator_Item
    | undefined;
  /** 虛帳退款處理完成/退款處理完成 */
  success:
    | SalesOrder_RefundIndicator_Item
    | undefined;
  /** 退款金額 */
  refundAmount: string;
  /** 退款方式 */
  refundChannel: SalesReturn_RefundChannel;
}

export interface SalesOrder_RefundIndicator_Item {
  /** 發生時間 */
  timestamp:
    | Date
    | undefined;
  /** 是否亮燈 */
  enable: boolean;
  /** 是否為虛帳退款, required when 退款處理完成 */
  isBalance: boolean;
}

export interface SalesOrders {
  orders: SalesOrder[];
}

export interface SalesReturn {
  /** 退單編號. Unique id generated by the server. Not visible to customers. */
  id: string;
  /** 訂單編號. Visible in the UI for customers. */
  orderId: string;
  /** 退單前端顯示狀態 */
  state: SalesReturn_FrState;
  /** 退單成立時間 */
  createTime:
    | Date
    | undefined;
  /** 付款類型 */
  paymentType: SalesOrder_PaymentType;
  /** 退貨明細 */
  items: SalesReturn_Item[];
  /** 最或商品數量加總 */
  totalQuantity: number;
  /** 退貨商品金額加總 */
  total: string;
  /** 運費 */
  shipmentFee: string;
  /** 應補退差額 */
  compensationAmount: string;
  /** 退款總金額 */
  refundAmount: string;
  /** 退貨期限 */
  returnRemindTimeout:
    | Date
    | undefined;
  /** 寄回物流公司 */
  returnChannel: SalesReturn_ReturnChannel;
  /** 寄回物流編號 */
  shipmentTrackingNumber: string;
  /** 退貨寄回期限（for UI only） */
  waitReturnRemindTimeout:
    | Date
    | undefined;
  /** 是否可以修改退貨 */
  canUpdateReturnItems: boolean;
  /** 完成寄件時間 */
  collectedTime:
    | Date
    | undefined;
  /** 退貨包裹刷讀時間 */
  scannedTime:
    | Date
    | undefined;
  /** 退款完成時間 */
  refundedTime:
    | Date
    | undefined;
  /** 退款渠道 */
  refundChannel: SalesReturn_RefundChannel;
  /** 退貨歷程 */
  indicator: SalesReturn_Indicator | undefined;
}

/** 前端顯示狀態 */
export enum SalesReturn_FrState {
  FR_STATE_UNKNOWN = 0,
  /** FR_WAIT_BUYER_RETURN_GOODS - 已完成退貨申請（待寄件） */
  FR_WAIT_BUYER_RETURN_GOODS = 1,
  /** FR_WAIT_SELLER_CONFIRM_GOODS - 已完成寄件 */
  FR_WAIT_SELLER_CONFIRM_GOODS = 2,
  /** FR_PROCESSING - 退款处理中/已收到退貨商品（退貨進行中） */
  FR_PROCESSING = 3,
  /** FR_SUCCESS - 退款成功 */
  FR_SUCCESS = 4,
}

export function salesReturn_FrStateFromJSON(object: any): SalesReturn_FrState {
  switch (object) {
    case 0:
    case "FR_STATE_UNKNOWN":
      return SalesReturn_FrState.FR_STATE_UNKNOWN;
    case 1:
    case "FR_WAIT_BUYER_RETURN_GOODS":
      return SalesReturn_FrState.FR_WAIT_BUYER_RETURN_GOODS;
    case 2:
    case "FR_WAIT_SELLER_CONFIRM_GOODS":
      return SalesReturn_FrState.FR_WAIT_SELLER_CONFIRM_GOODS;
    case 3:
    case "FR_PROCESSING":
      return SalesReturn_FrState.FR_PROCESSING;
    case 4:
    case "FR_SUCCESS":
      return SalesReturn_FrState.FR_SUCCESS;
    default:
      throw new tsProtoGlobalThis.Error("Unrecognized enum value " + object + " for enum SalesReturn_FrState");
  }
}

export function salesReturn_FrStateToJSON(object: SalesReturn_FrState): string {
  switch (object) {
    case SalesReturn_FrState.FR_STATE_UNKNOWN:
      return "FR_STATE_UNKNOWN";
    case SalesReturn_FrState.FR_WAIT_BUYER_RETURN_GOODS:
      return "FR_WAIT_BUYER_RETURN_GOODS";
    case SalesReturn_FrState.FR_WAIT_SELLER_CONFIRM_GOODS:
      return "FR_WAIT_SELLER_CONFIRM_GOODS";
    case SalesReturn_FrState.FR_PROCESSING:
      return "FR_PROCESSING";
    case SalesReturn_FrState.FR_SUCCESS:
      return "FR_SUCCESS";
    default:
      throw new tsProtoGlobalThis.Error("Unrecognized enum value " + object + " for enum SalesReturn_FrState");
  }
}

/** 退貨明細 */
export interface SalesReturn_Item {
  /** 訂單品項編號. Unique id generated by the server. Not visible to customers. */
  orderItemId: string;
  /** 商品編號 */
  skuId: string;
  /** 商品名稱 */
  productName: string;
  /** 商品圖片. Image 360 pixels. */
  productImage: string;
  /** 顏色 */
  colorName: string;
  /** 尺寸 */
  sizeName: string;
  /** 尺碼後綴 */
  sizeSuffix: string;
  /** 單價 */
  unitPrice: string;
  /** 折扣後單價 */
  discountedUnitPrice: string;
  /** 活動名稱 */
  promotionName: string;
  /** 退貨數量 */
  quantity: number;
  /** 小計加總 */
  subtotal: string;
  /** 退貨原因 */
  reason: string;
  /** 退貨原因備註 */
  note: string;
}

/** 退單流程 */
export interface SalesReturn_Indicator {
  /** 申請退貨 */
  new:
    | SalesReturn_Indicator_Item
    | undefined;
  /** 已完成寄件 */
  waitConfirm:
    | SalesReturn_Indicator_Item
    | undefined;
  /** 已收到退貨商品 */
  processing:
    | SalesReturn_Indicator_Item
    | undefined;
  /** 完成退款 */
  success: SalesReturn_Indicator_Item | undefined;
}

export interface SalesReturn_Indicator_Item {
  title: string;
  desc: string;
  timestamp: Date | undefined;
  enable: boolean;
}

function createBaseSalesOrder(): SalesOrder {
  return {
    id: "",
    state: 0,
    paymentType: 0,
    createTime: undefined,
    paymentRemindTimeout: undefined,
    paymentTime: undefined,
    expectedOutflowTime: undefined,
    claimedTime: undefined,
    returnRemindTimeout: undefined,
    storeLastPickupTime: undefined,
    cancelTime: undefined,
    refundCompletedTime: undefined,
    logisticsCompany: 0,
    shipmentTrackingNumber: "",
    waitingForShipping: false,
    noPickup: false,
    hasCreditNote: false,
    hasWinnerReceipt: false,
    csOffline: false,
    receiptMethod: 0,
    receiptContent: "",
    recipientInfo: undefined,
    invoiceInfo: undefined,
    refundIndicator: undefined,
    cartInfo: undefined,
    finalCartInfo: undefined,
    buttons: undefined,
  };
}

export const SalesOrder = {
  fromJSON(object: any): SalesOrder {
    return {
      id: isSet(object.id) ? String(object.id) : "",
      state: isSet(object.state) ? salesOrder_FrStateFromJSON(object.state) : 0,
      paymentType: isSet(object.paymentType) ? salesOrder_PaymentTypeFromJSON(object.paymentType) : 0,
      createTime: isSet(object.createTime) ? fromJsonTimestamp(object.createTime) : undefined,
      paymentRemindTimeout: isSet(object.paymentRemindTimeout)
        ? fromJsonTimestamp(object.paymentRemindTimeout)
        : undefined,
      paymentTime: isSet(object.paymentTime) ? fromJsonTimestamp(object.paymentTime) : undefined,
      expectedOutflowTime: isSet(object.expectedOutflowTime)
        ? fromJsonTimestamp(object.expectedOutflowTime)
        : undefined,
      claimedTime: isSet(object.claimedTime) ? fromJsonTimestamp(object.claimedTime) : undefined,
      returnRemindTimeout: isSet(object.returnRemindTimeout)
        ? fromJsonTimestamp(object.returnRemindTimeout)
        : undefined,
      storeLastPickupTime: isSet(object.storeLastPickupTime)
        ? fromJsonTimestamp(object.storeLastPickupTime)
        : undefined,
      cancelTime: isSet(object.cancelTime) ? fromJsonTimestamp(object.cancelTime) : undefined,
      refundCompletedTime: isSet(object.refundCompletedTime)
        ? fromJsonTimestamp(object.refundCompletedTime)
        : undefined,
      logisticsCompany: isSet(object.logisticsCompany)
        ? salesOrder_LogisticsCompanyFromJSON(object.logisticsCompany)
        : 0,
      shipmentTrackingNumber: isSet(object.shipmentTrackingNumber) ? String(object.shipmentTrackingNumber) : "",
      waitingForShipping: isSet(object.waitingForShipping) ? Boolean(object.waitingForShipping) : false,
      noPickup: isSet(object.noPickup) ? Boolean(object.noPickup) : false,
      hasCreditNote: isSet(object.hasCreditNote) ? Boolean(object.hasCreditNote) : false,
      hasWinnerReceipt: isSet(object.hasWinnerReceipt) ? Boolean(object.hasWinnerReceipt) : false,
      csOffline: isSet(object.csOffline) ? Boolean(object.csOffline) : false,
      receiptMethod: isSet(object.receiptMethod) ? salesOrder_ReceiptMethodFromJSON(object.receiptMethod) : 0,
      receiptContent: isSet(object.receiptContent) ? String(object.receiptContent) : "",
      recipientInfo: isSet(object.recipientInfo) ? Recipient.fromJSON(object.recipientInfo) : undefined,
      invoiceInfo: isSet(object.invoiceInfo) ? SalesOrder_Invoice.fromJSON(object.invoiceInfo) : undefined,
      refundIndicator: isSet(object.refundIndicator)
        ? SalesOrder_RefundIndicator.fromJSON(object.refundIndicator)
        : undefined,
      cartInfo: isSet(object.cartInfo) ? SalesOrder_CartInfo.fromJSON(object.cartInfo) : undefined,
      finalCartInfo: isSet(object.finalCartInfo) ? SalesOrder_CartInfo.fromJSON(object.finalCartInfo) : undefined,
      buttons: isSet(object.buttons) ? SalesOrder_Buttons.fromJSON(object.buttons) : undefined,
    };
  },

  toJSON(message: SalesOrder): unknown {
    const obj: any = {};
    message.id !== undefined && (obj.id = message.id);
    message.state !== undefined && (obj.state = salesOrder_FrStateToJSON(message.state));
    message.paymentType !== undefined && (obj.paymentType = salesOrder_PaymentTypeToJSON(message.paymentType));
    message.createTime !== undefined && (obj.createTime = message.createTime.toISOString());
    message.paymentRemindTimeout !== undefined &&
      (obj.paymentRemindTimeout = message.paymentRemindTimeout.toISOString());
    message.paymentTime !== undefined && (obj.paymentTime = message.paymentTime.toISOString());
    message.expectedOutflowTime !== undefined && (obj.expectedOutflowTime = message.expectedOutflowTime.toISOString());
    message.claimedTime !== undefined && (obj.claimedTime = message.claimedTime.toISOString());
    message.returnRemindTimeout !== undefined && (obj.returnRemindTimeout = message.returnRemindTimeout.toISOString());
    message.storeLastPickupTime !== undefined && (obj.storeLastPickupTime = message.storeLastPickupTime.toISOString());
    message.cancelTime !== undefined && (obj.cancelTime = message.cancelTime.toISOString());
    message.refundCompletedTime !== undefined && (obj.refundCompletedTime = message.refundCompletedTime.toISOString());
    message.logisticsCompany !== undefined &&
      (obj.logisticsCompany = salesOrder_LogisticsCompanyToJSON(message.logisticsCompany));
    message.shipmentTrackingNumber !== undefined && (obj.shipmentTrackingNumber = message.shipmentTrackingNumber);
    message.waitingForShipping !== undefined && (obj.waitingForShipping = message.waitingForShipping);
    message.noPickup !== undefined && (obj.noPickup = message.noPickup);
    message.hasCreditNote !== undefined && (obj.hasCreditNote = message.hasCreditNote);
    message.hasWinnerReceipt !== undefined && (obj.hasWinnerReceipt = message.hasWinnerReceipt);
    message.csOffline !== undefined && (obj.csOffline = message.csOffline);
    message.receiptMethod !== undefined && (obj.receiptMethod = salesOrder_ReceiptMethodToJSON(message.receiptMethod));
    message.receiptContent !== undefined && (obj.receiptContent = message.receiptContent);
    message.recipientInfo !== undefined &&
      (obj.recipientInfo = message.recipientInfo ? Recipient.toJSON(message.recipientInfo) : undefined);
    message.invoiceInfo !== undefined &&
      (obj.invoiceInfo = message.invoiceInfo ? SalesOrder_Invoice.toJSON(message.invoiceInfo) : undefined);
    message.refundIndicator !== undefined && (obj.refundIndicator = message.refundIndicator
      ? SalesOrder_RefundIndicator.toJSON(message.refundIndicator)
      : undefined);
    message.cartInfo !== undefined &&
      (obj.cartInfo = message.cartInfo ? SalesOrder_CartInfo.toJSON(message.cartInfo) : undefined);
    message.finalCartInfo !== undefined &&
      (obj.finalCartInfo = message.finalCartInfo ? SalesOrder_CartInfo.toJSON(message.finalCartInfo) : undefined);
    message.buttons !== undefined &&
      (obj.buttons = message.buttons ? SalesOrder_Buttons.toJSON(message.buttons) : undefined);
    return obj;
  },

  create<I extends Exact<DeepPartial<SalesOrder>, I>>(base?: I): SalesOrder {
    return SalesOrder.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<SalesOrder>, I>>(object: I): SalesOrder {
    const message = createBaseSalesOrder();
    message.id = object.id ?? "";
    message.state = object.state ?? 0;
    message.paymentType = object.paymentType ?? 0;
    message.createTime = object.createTime ?? undefined;
    message.paymentRemindTimeout = object.paymentRemindTimeout ?? undefined;
    message.paymentTime = object.paymentTime ?? undefined;
    message.expectedOutflowTime = object.expectedOutflowTime ?? undefined;
    message.claimedTime = object.claimedTime ?? undefined;
    message.returnRemindTimeout = object.returnRemindTimeout ?? undefined;
    message.storeLastPickupTime = object.storeLastPickupTime ?? undefined;
    message.cancelTime = object.cancelTime ?? undefined;
    message.refundCompletedTime = object.refundCompletedTime ?? undefined;
    message.logisticsCompany = object.logisticsCompany ?? 0;
    message.shipmentTrackingNumber = object.shipmentTrackingNumber ?? "";
    message.waitingForShipping = object.waitingForShipping ?? false;
    message.noPickup = object.noPickup ?? false;
    message.hasCreditNote = object.hasCreditNote ?? false;
    message.hasWinnerReceipt = object.hasWinnerReceipt ?? false;
    message.csOffline = object.csOffline ?? false;
    message.receiptMethod = object.receiptMethod ?? 0;
    message.receiptContent = object.receiptContent ?? "";
    message.recipientInfo = (object.recipientInfo !== undefined && object.recipientInfo !== null)
      ? Recipient.fromPartial(object.recipientInfo)
      : undefined;
    message.invoiceInfo = (object.invoiceInfo !== undefined && object.invoiceInfo !== null)
      ? SalesOrder_Invoice.fromPartial(object.invoiceInfo)
      : undefined;
    message.refundIndicator = (object.refundIndicator !== undefined && object.refundIndicator !== null)
      ? SalesOrder_RefundIndicator.fromPartial(object.refundIndicator)
      : undefined;
    message.cartInfo = (object.cartInfo !== undefined && object.cartInfo !== null)
      ? SalesOrder_CartInfo.fromPartial(object.cartInfo)
      : undefined;
    message.finalCartInfo = (object.finalCartInfo !== undefined && object.finalCartInfo !== null)
      ? SalesOrder_CartInfo.fromPartial(object.finalCartInfo)
      : undefined;
    message.buttons = (object.buttons !== undefined && object.buttons !== null)
      ? SalesOrder_Buttons.fromPartial(object.buttons)
      : undefined;
    return message;
  },
};

function createBaseSalesOrder_Item(): SalesOrder_Item {
  return {
    id: "",
    skuId: "",
    productName: "",
    productImage: "",
    colorName: "",
    sizeName: "",
    sizeSuffix: "",
    unitPrice: "",
    discountedUnitPrice: "",
    promotionName: "",
    promotionAmortizedDeduction: "",
    quantity: 0,
    subtotal: "",
    unitPriceSubtotal: "",
    published: false,
  };
}

export const SalesOrder_Item = {
  fromJSON(object: any): SalesOrder_Item {
    return {
      id: isSet(object.id) ? String(object.id) : "",
      skuId: isSet(object.skuId) ? String(object.skuId) : "",
      productName: isSet(object.productName) ? String(object.productName) : "",
      productImage: isSet(object.productImage) ? String(object.productImage) : "",
      colorName: isSet(object.colorName) ? String(object.colorName) : "",
      sizeName: isSet(object.sizeName) ? String(object.sizeName) : "",
      sizeSuffix: isSet(object.sizeSuffix) ? String(object.sizeSuffix) : "",
      unitPrice: isSet(object.unitPrice) ? String(object.unitPrice) : "",
      discountedUnitPrice: isSet(object.discountedUnitPrice) ? String(object.discountedUnitPrice) : "",
      promotionName: isSet(object.promotionName) ? String(object.promotionName) : "",
      promotionAmortizedDeduction: isSet(object.promotionAmortizedDeduction)
        ? String(object.promotionAmortizedDeduction)
        : "",
      quantity: isSet(object.quantity) ? Number(object.quantity) : 0,
      subtotal: isSet(object.subtotal) ? String(object.subtotal) : "",
      unitPriceSubtotal: isSet(object.unitPriceSubtotal) ? String(object.unitPriceSubtotal) : "",
      published: isSet(object.published) ? Boolean(object.published) : false,
    };
  },

  toJSON(message: SalesOrder_Item): unknown {
    const obj: any = {};
    message.id !== undefined && (obj.id = message.id);
    message.skuId !== undefined && (obj.skuId = message.skuId);
    message.productName !== undefined && (obj.productName = message.productName);
    message.productImage !== undefined && (obj.productImage = message.productImage);
    message.colorName !== undefined && (obj.colorName = message.colorName);
    message.sizeName !== undefined && (obj.sizeName = message.sizeName);
    message.sizeSuffix !== undefined && (obj.sizeSuffix = message.sizeSuffix);
    message.unitPrice !== undefined && (obj.unitPrice = message.unitPrice);
    message.discountedUnitPrice !== undefined && (obj.discountedUnitPrice = message.discountedUnitPrice);
    message.promotionName !== undefined && (obj.promotionName = message.promotionName);
    message.promotionAmortizedDeduction !== undefined &&
      (obj.promotionAmortizedDeduction = message.promotionAmortizedDeduction);
    message.quantity !== undefined && (obj.quantity = Math.round(message.quantity));
    message.subtotal !== undefined && (obj.subtotal = message.subtotal);
    message.unitPriceSubtotal !== undefined && (obj.unitPriceSubtotal = message.unitPriceSubtotal);
    message.published !== undefined && (obj.published = message.published);
    return obj;
  },

  create<I extends Exact<DeepPartial<SalesOrder_Item>, I>>(base?: I): SalesOrder_Item {
    return SalesOrder_Item.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<SalesOrder_Item>, I>>(object: I): SalesOrder_Item {
    const message = createBaseSalesOrder_Item();
    message.id = object.id ?? "";
    message.skuId = object.skuId ?? "";
    message.productName = object.productName ?? "";
    message.productImage = object.productImage ?? "";
    message.colorName = object.colorName ?? "";
    message.sizeName = object.sizeName ?? "";
    message.sizeSuffix = object.sizeSuffix ?? "";
    message.unitPrice = object.unitPrice ?? "";
    message.discountedUnitPrice = object.discountedUnitPrice ?? "";
    message.promotionName = object.promotionName ?? "";
    message.promotionAmortizedDeduction = object.promotionAmortizedDeduction ?? "";
    message.quantity = object.quantity ?? 0;
    message.subtotal = object.subtotal ?? "";
    message.unitPriceSubtotal = object.unitPriceSubtotal ?? "";
    message.published = object.published ?? false;
    return message;
  },
};

function createBaseSalesOrder_CartInfo(): SalesOrder_CartInfo {
  return {
    items: [],
    quantity: 0,
    subtotal: "",
    promotionDeduction: "",
    shipping: "",
    balance: "",
    total: "",
    allItemsRated: false,
    exchangeRateUsdNtd: "",
    totalUsd: "",
  };
}

export const SalesOrder_CartInfo = {
  fromJSON(object: any): SalesOrder_CartInfo {
    return {
      items: Array.isArray(object?.items) ? object.items.map((e: any) => SalesOrder_Item.fromJSON(e)) : [],
      quantity: isSet(object.quantity) ? Number(object.quantity) : 0,
      subtotal: isSet(object.subtotal) ? String(object.subtotal) : "",
      promotionDeduction: isSet(object.promotionDeduction) ? String(object.promotionDeduction) : "",
      shipping: isSet(object.shipping) ? String(object.shipping) : "",
      balance: isSet(object.balance) ? String(object.balance) : "",
      total: isSet(object.total) ? String(object.total) : "",
      allItemsRated: isSet(object.allItemsRated) ? Boolean(object.allItemsRated) : false,
      exchangeRateUsdNtd: isSet(object.exchangeRateUsdNtd) ? String(object.exchangeRateUsdNtd) : "",
      totalUsd: isSet(object.totalUsd) ? String(object.totalUsd) : "",
    };
  },

  toJSON(message: SalesOrder_CartInfo): unknown {
    const obj: any = {};
    if (message.items) {
      obj.items = message.items.map((e) => e ? SalesOrder_Item.toJSON(e) : undefined);
    } else {
      obj.items = [];
    }
    message.quantity !== undefined && (obj.quantity = Math.round(message.quantity));
    message.subtotal !== undefined && (obj.subtotal = message.subtotal);
    message.promotionDeduction !== undefined && (obj.promotionDeduction = message.promotionDeduction);
    message.shipping !== undefined && (obj.shipping = message.shipping);
    message.balance !== undefined && (obj.balance = message.balance);
    message.total !== undefined && (obj.total = message.total);
    message.allItemsRated !== undefined && (obj.allItemsRated = message.allItemsRated);
    message.exchangeRateUsdNtd !== undefined && (obj.exchangeRateUsdNtd = message.exchangeRateUsdNtd);
    message.totalUsd !== undefined && (obj.totalUsd = message.totalUsd);
    return obj;
  },

  create<I extends Exact<DeepPartial<SalesOrder_CartInfo>, I>>(base?: I): SalesOrder_CartInfo {
    return SalesOrder_CartInfo.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<SalesOrder_CartInfo>, I>>(object: I): SalesOrder_CartInfo {
    const message = createBaseSalesOrder_CartInfo();
    message.items = object.items?.map((e) => SalesOrder_Item.fromPartial(e)) || [];
    message.quantity = object.quantity ?? 0;
    message.subtotal = object.subtotal ?? "";
    message.promotionDeduction = object.promotionDeduction ?? "";
    message.shipping = object.shipping ?? "";
    message.balance = object.balance ?? "";
    message.total = object.total ?? "";
    message.allItemsRated = object.allItemsRated ?? false;
    message.exchangeRateUsdNtd = object.exchangeRateUsdNtd ?? "";
    message.totalUsd = object.totalUsd ?? "";
    return message;
  },
};

function createBaseSalesOrder_Buttons(): SalesOrder_Buttons {
  return {
    csBtn: false,
    cancelTradeBtn: false,
    payBtn: false,
    logisticsBtn: false,
    rateBtn: false,
    deleteTradeBtn: false,
    buyAgainBtn: false,
    applyReturnBtn: false,
    returnInfoBtn: false,
    downloadReceiptBtn: false,
  };
}

export const SalesOrder_Buttons = {
  fromJSON(object: any): SalesOrder_Buttons {
    return {
      csBtn: isSet(object.csBtn) ? Boolean(object.csBtn) : false,
      cancelTradeBtn: isSet(object.cancelTradeBtn) ? Boolean(object.cancelTradeBtn) : false,
      payBtn: isSet(object.payBtn) ? Boolean(object.payBtn) : false,
      logisticsBtn: isSet(object.logisticsBtn) ? Boolean(object.logisticsBtn) : false,
      rateBtn: isSet(object.rateBtn) ? Boolean(object.rateBtn) : false,
      deleteTradeBtn: isSet(object.deleteTradeBtn) ? Boolean(object.deleteTradeBtn) : false,
      buyAgainBtn: isSet(object.buyAgainBtn) ? Boolean(object.buyAgainBtn) : false,
      applyReturnBtn: isSet(object.applyReturnBtn) ? Boolean(object.applyReturnBtn) : false,
      returnInfoBtn: isSet(object.returnInfoBtn) ? Boolean(object.returnInfoBtn) : false,
      downloadReceiptBtn: isSet(object.downloadReceiptBtn) ? Boolean(object.downloadReceiptBtn) : false,
    };
  },

  toJSON(message: SalesOrder_Buttons): unknown {
    const obj: any = {};
    message.csBtn !== undefined && (obj.csBtn = message.csBtn);
    message.cancelTradeBtn !== undefined && (obj.cancelTradeBtn = message.cancelTradeBtn);
    message.payBtn !== undefined && (obj.payBtn = message.payBtn);
    message.logisticsBtn !== undefined && (obj.logisticsBtn = message.logisticsBtn);
    message.rateBtn !== undefined && (obj.rateBtn = message.rateBtn);
    message.deleteTradeBtn !== undefined && (obj.deleteTradeBtn = message.deleteTradeBtn);
    message.buyAgainBtn !== undefined && (obj.buyAgainBtn = message.buyAgainBtn);
    message.applyReturnBtn !== undefined && (obj.applyReturnBtn = message.applyReturnBtn);
    message.returnInfoBtn !== undefined && (obj.returnInfoBtn = message.returnInfoBtn);
    message.downloadReceiptBtn !== undefined && (obj.downloadReceiptBtn = message.downloadReceiptBtn);
    return obj;
  },

  create<I extends Exact<DeepPartial<SalesOrder_Buttons>, I>>(base?: I): SalesOrder_Buttons {
    return SalesOrder_Buttons.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<SalesOrder_Buttons>, I>>(object: I): SalesOrder_Buttons {
    const message = createBaseSalesOrder_Buttons();
    message.csBtn = object.csBtn ?? false;
    message.cancelTradeBtn = object.cancelTradeBtn ?? false;
    message.payBtn = object.payBtn ?? false;
    message.logisticsBtn = object.logisticsBtn ?? false;
    message.rateBtn = object.rateBtn ?? false;
    message.deleteTradeBtn = object.deleteTradeBtn ?? false;
    message.buyAgainBtn = object.buyAgainBtn ?? false;
    message.applyReturnBtn = object.applyReturnBtn ?? false;
    message.returnInfoBtn = object.returnInfoBtn ?? false;
    message.downloadReceiptBtn = object.downloadReceiptBtn ?? false;
    return message;
  },
};

function createBaseSalesOrder_LogisticsRoute(): SalesOrder_LogisticsRoute {
  return {
    type: 0,
    timestamp: undefined,
    url: "",
    expectedOutflowTime: undefined,
    storeDeliveredTime: undefined,
    storeLastPickupTime: undefined,
    desc: "",
  };
}

export const SalesOrder_LogisticsRoute = {
  fromJSON(object: any): SalesOrder_LogisticsRoute {
    return {
      type: isSet(object.type) ? salesOrder_LogisticsRoute_TypeFromJSON(object.type) : 0,
      timestamp: isSet(object.timestamp) ? fromJsonTimestamp(object.timestamp) : undefined,
      url: isSet(object.url) ? String(object.url) : "",
      expectedOutflowTime: isSet(object.expectedOutflowTime)
        ? fromJsonTimestamp(object.expectedOutflowTime)
        : undefined,
      storeDeliveredTime: isSet(object.storeDeliveredTime) ? fromJsonTimestamp(object.storeDeliveredTime) : undefined,
      storeLastPickupTime: isSet(object.storeLastPickupTime)
        ? fromJsonTimestamp(object.storeLastPickupTime)
        : undefined,
      desc: isSet(object.desc) ? String(object.desc) : "",
    };
  },

  toJSON(message: SalesOrder_LogisticsRoute): unknown {
    const obj: any = {};
    message.type !== undefined && (obj.type = salesOrder_LogisticsRoute_TypeToJSON(message.type));
    message.timestamp !== undefined && (obj.timestamp = message.timestamp.toISOString());
    message.url !== undefined && (obj.url = message.url);
    message.expectedOutflowTime !== undefined && (obj.expectedOutflowTime = message.expectedOutflowTime.toISOString());
    message.storeDeliveredTime !== undefined && (obj.storeDeliveredTime = message.storeDeliveredTime.toISOString());
    message.storeLastPickupTime !== undefined && (obj.storeLastPickupTime = message.storeLastPickupTime.toISOString());
    message.desc !== undefined && (obj.desc = message.desc);
    return obj;
  },

  create<I extends Exact<DeepPartial<SalesOrder_LogisticsRoute>, I>>(base?: I): SalesOrder_LogisticsRoute {
    return SalesOrder_LogisticsRoute.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<SalesOrder_LogisticsRoute>, I>>(object: I): SalesOrder_LogisticsRoute {
    const message = createBaseSalesOrder_LogisticsRoute();
    message.type = object.type ?? 0;
    message.timestamp = object.timestamp ?? undefined;
    message.url = object.url ?? "";
    message.expectedOutflowTime = object.expectedOutflowTime ?? undefined;
    message.storeDeliveredTime = object.storeDeliveredTime ?? undefined;
    message.storeLastPickupTime = object.storeLastPickupTime ?? undefined;
    message.desc = object.desc ?? "";
    return message;
  },
};

function createBaseSalesOrder_Invoice(): SalesOrder_Invoice {
  return {
    rocYear: "",
    period: "",
    invoiceNo: "",
    invoiceTime: "",
    triplicateCode: "",
    randomCode: "",
    amount: "",
    sellerTaxNo: "",
    buyerTaxNo: "",
    barcode: "",
    leftQrcode: "",
    rightQrcode: "",
    amountBeforeTax: "",
    taxCategory: "",
    taxAmount: "",
    memo: "",
    isCancel: false,
  };
}

export const SalesOrder_Invoice = {
  fromJSON(object: any): SalesOrder_Invoice {
    return {
      rocYear: isSet(object.rocYear) ? String(object.rocYear) : "",
      period: isSet(object.period) ? String(object.period) : "",
      invoiceNo: isSet(object.invoiceNo) ? String(object.invoiceNo) : "",
      invoiceTime: isSet(object.invoiceTime) ? String(object.invoiceTime) : "",
      triplicateCode: isSet(object.triplicateCode) ? String(object.triplicateCode) : "",
      randomCode: isSet(object.randomCode) ? String(object.randomCode) : "",
      amount: isSet(object.amount) ? String(object.amount) : "",
      sellerTaxNo: isSet(object.sellerTaxNo) ? String(object.sellerTaxNo) : "",
      buyerTaxNo: isSet(object.buyerTaxNo) ? String(object.buyerTaxNo) : "",
      barcode: isSet(object.barcode) ? String(object.barcode) : "",
      leftQrcode: isSet(object.leftQrcode) ? String(object.leftQrcode) : "",
      rightQrcode: isSet(object.rightQrcode) ? String(object.rightQrcode) : "",
      amountBeforeTax: isSet(object.amountBeforeTax) ? String(object.amountBeforeTax) : "",
      taxCategory: isSet(object.taxCategory) ? String(object.taxCategory) : "",
      taxAmount: isSet(object.taxAmount) ? String(object.taxAmount) : "",
      memo: isSet(object.memo) ? String(object.memo) : "",
      isCancel: isSet(object.isCancel) ? Boolean(object.isCancel) : false,
    };
  },

  toJSON(message: SalesOrder_Invoice): unknown {
    const obj: any = {};
    message.rocYear !== undefined && (obj.rocYear = message.rocYear);
    message.period !== undefined && (obj.period = message.period);
    message.invoiceNo !== undefined && (obj.invoiceNo = message.invoiceNo);
    message.invoiceTime !== undefined && (obj.invoiceTime = message.invoiceTime);
    message.triplicateCode !== undefined && (obj.triplicateCode = message.triplicateCode);
    message.randomCode !== undefined && (obj.randomCode = message.randomCode);
    message.amount !== undefined && (obj.amount = message.amount);
    message.sellerTaxNo !== undefined && (obj.sellerTaxNo = message.sellerTaxNo);
    message.buyerTaxNo !== undefined && (obj.buyerTaxNo = message.buyerTaxNo);
    message.barcode !== undefined && (obj.barcode = message.barcode);
    message.leftQrcode !== undefined && (obj.leftQrcode = message.leftQrcode);
    message.rightQrcode !== undefined && (obj.rightQrcode = message.rightQrcode);
    message.amountBeforeTax !== undefined && (obj.amountBeforeTax = message.amountBeforeTax);
    message.taxCategory !== undefined && (obj.taxCategory = message.taxCategory);
    message.taxAmount !== undefined && (obj.taxAmount = message.taxAmount);
    message.memo !== undefined && (obj.memo = message.memo);
    message.isCancel !== undefined && (obj.isCancel = message.isCancel);
    return obj;
  },

  create<I extends Exact<DeepPartial<SalesOrder_Invoice>, I>>(base?: I): SalesOrder_Invoice {
    return SalesOrder_Invoice.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<SalesOrder_Invoice>, I>>(object: I): SalesOrder_Invoice {
    const message = createBaseSalesOrder_Invoice();
    message.rocYear = object.rocYear ?? "";
    message.period = object.period ?? "";
    message.invoiceNo = object.invoiceNo ?? "";
    message.invoiceTime = object.invoiceTime ?? "";
    message.triplicateCode = object.triplicateCode ?? "";
    message.randomCode = object.randomCode ?? "";
    message.amount = object.amount ?? "";
    message.sellerTaxNo = object.sellerTaxNo ?? "";
    message.buyerTaxNo = object.buyerTaxNo ?? "";
    message.barcode = object.barcode ?? "";
    message.leftQrcode = object.leftQrcode ?? "";
    message.rightQrcode = object.rightQrcode ?? "";
    message.amountBeforeTax = object.amountBeforeTax ?? "";
    message.taxCategory = object.taxCategory ?? "";
    message.taxAmount = object.taxAmount ?? "";
    message.memo = object.memo ?? "";
    message.isCancel = object.isCancel ?? false;
    return message;
  },
};

function createBaseSalesOrder_RefundIndicator(): SalesOrder_RefundIndicator {
  return {
    new: undefined,
    handling: undefined,
    pendingRefund: undefined,
    success: undefined,
    refundAmount: "",
    refundChannel: 0,
  };
}

export const SalesOrder_RefundIndicator = {
  fromJSON(object: any): SalesOrder_RefundIndicator {
    return {
      new: isSet(object.new) ? SalesOrder_RefundIndicator_Item.fromJSON(object.new) : undefined,
      handling: isSet(object.handling) ? SalesOrder_RefundIndicator_Item.fromJSON(object.handling) : undefined,
      pendingRefund: isSet(object.pendingRefund)
        ? SalesOrder_RefundIndicator_Item.fromJSON(object.pendingRefund)
        : undefined,
      success: isSet(object.success) ? SalesOrder_RefundIndicator_Item.fromJSON(object.success) : undefined,
      refundAmount: isSet(object.refundAmount) ? String(object.refundAmount) : "",
      refundChannel: isSet(object.refundChannel) ? salesReturn_RefundChannelFromJSON(object.refundChannel) : 0,
    };
  },

  toJSON(message: SalesOrder_RefundIndicator): unknown {
    const obj: any = {};
    message.new !== undefined &&
      (obj.new = message.new ? SalesOrder_RefundIndicator_Item.toJSON(message.new) : undefined);
    message.handling !== undefined &&
      (obj.handling = message.handling ? SalesOrder_RefundIndicator_Item.toJSON(message.handling) : undefined);
    message.pendingRefund !== undefined && (obj.pendingRefund = message.pendingRefund
      ? SalesOrder_RefundIndicator_Item.toJSON(message.pendingRefund)
      : undefined);
    message.success !== undefined &&
      (obj.success = message.success ? SalesOrder_RefundIndicator_Item.toJSON(message.success) : undefined);
    message.refundAmount !== undefined && (obj.refundAmount = message.refundAmount);
    message.refundChannel !== undefined && (obj.refundChannel = salesReturn_RefundChannelToJSON(message.refundChannel));
    return obj;
  },

  create<I extends Exact<DeepPartial<SalesOrder_RefundIndicator>, I>>(base?: I): SalesOrder_RefundIndicator {
    return SalesOrder_RefundIndicator.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<SalesOrder_RefundIndicator>, I>>(object: I): SalesOrder_RefundIndicator {
    const message = createBaseSalesOrder_RefundIndicator();
    message.new = (object.new !== undefined && object.new !== null)
      ? SalesOrder_RefundIndicator_Item.fromPartial(object.new)
      : undefined;
    message.handling = (object.handling !== undefined && object.handling !== null)
      ? SalesOrder_RefundIndicator_Item.fromPartial(object.handling)
      : undefined;
    message.pendingRefund = (object.pendingRefund !== undefined && object.pendingRefund !== null)
      ? SalesOrder_RefundIndicator_Item.fromPartial(object.pendingRefund)
      : undefined;
    message.success = (object.success !== undefined && object.success !== null)
      ? SalesOrder_RefundIndicator_Item.fromPartial(object.success)
      : undefined;
    message.refundAmount = object.refundAmount ?? "";
    message.refundChannel = object.refundChannel ?? 0;
    return message;
  },
};

function createBaseSalesOrder_RefundIndicator_Item(): SalesOrder_RefundIndicator_Item {
  return { timestamp: undefined, enable: false, isBalance: false };
}

export const SalesOrder_RefundIndicator_Item = {
  fromJSON(object: any): SalesOrder_RefundIndicator_Item {
    return {
      timestamp: isSet(object.timestamp) ? fromJsonTimestamp(object.timestamp) : undefined,
      enable: isSet(object.enable) ? Boolean(object.enable) : false,
      isBalance: isSet(object.isBalance) ? Boolean(object.isBalance) : false,
    };
  },

  toJSON(message: SalesOrder_RefundIndicator_Item): unknown {
    const obj: any = {};
    message.timestamp !== undefined && (obj.timestamp = message.timestamp.toISOString());
    message.enable !== undefined && (obj.enable = message.enable);
    message.isBalance !== undefined && (obj.isBalance = message.isBalance);
    return obj;
  },

  create<I extends Exact<DeepPartial<SalesOrder_RefundIndicator_Item>, I>>(base?: I): SalesOrder_RefundIndicator_Item {
    return SalesOrder_RefundIndicator_Item.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<SalesOrder_RefundIndicator_Item>, I>>(
    object: I,
  ): SalesOrder_RefundIndicator_Item {
    const message = createBaseSalesOrder_RefundIndicator_Item();
    message.timestamp = object.timestamp ?? undefined;
    message.enable = object.enable ?? false;
    message.isBalance = object.isBalance ?? false;
    return message;
  },
};

function createBaseSalesOrders(): SalesOrders {
  return { orders: [] };
}

export const SalesOrders = {
  fromJSON(object: any): SalesOrders {
    return { orders: Array.isArray(object?.orders) ? object.orders.map((e: any) => SalesOrder.fromJSON(e)) : [] };
  },

  toJSON(message: SalesOrders): unknown {
    const obj: any = {};
    if (message.orders) {
      obj.orders = message.orders.map((e) => e ? SalesOrder.toJSON(e) : undefined);
    } else {
      obj.orders = [];
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<SalesOrders>, I>>(base?: I): SalesOrders {
    return SalesOrders.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<SalesOrders>, I>>(object: I): SalesOrders {
    const message = createBaseSalesOrders();
    message.orders = object.orders?.map((e) => SalesOrder.fromPartial(e)) || [];
    return message;
  },
};

function createBaseSalesReturn(): SalesReturn {
  return {
    id: "",
    orderId: "",
    state: 0,
    createTime: undefined,
    paymentType: 0,
    items: [],
    totalQuantity: 0,
    total: "",
    shipmentFee: "",
    compensationAmount: "",
    refundAmount: "",
    returnRemindTimeout: undefined,
    returnChannel: 0,
    shipmentTrackingNumber: "",
    waitReturnRemindTimeout: undefined,
    canUpdateReturnItems: false,
    collectedTime: undefined,
    scannedTime: undefined,
    refundedTime: undefined,
    refundChannel: 0,
    indicator: undefined,
  };
}

export const SalesReturn = {
  fromJSON(object: any): SalesReturn {
    return {
      id: isSet(object.id) ? String(object.id) : "",
      orderId: isSet(object.orderId) ? String(object.orderId) : "",
      state: isSet(object.state) ? salesReturn_FrStateFromJSON(object.state) : 0,
      createTime: isSet(object.createTime) ? fromJsonTimestamp(object.createTime) : undefined,
      paymentType: isSet(object.paymentType) ? salesOrder_PaymentTypeFromJSON(object.paymentType) : 0,
      items: Array.isArray(object?.items) ? object.items.map((e: any) => SalesReturn_Item.fromJSON(e)) : [],
      totalQuantity: isSet(object.totalQuantity) ? Number(object.totalQuantity) : 0,
      total: isSet(object.total) ? String(object.total) : "",
      shipmentFee: isSet(object.shipmentFee) ? String(object.shipmentFee) : "",
      compensationAmount: isSet(object.compensationAmount) ? String(object.compensationAmount) : "",
      refundAmount: isSet(object.refundAmount) ? String(object.refundAmount) : "",
      returnRemindTimeout: isSet(object.returnRemindTimeout)
        ? fromJsonTimestamp(object.returnRemindTimeout)
        : undefined,
      returnChannel: isSet(object.returnChannel) ? salesReturn_ReturnChannelFromJSON(object.returnChannel) : 0,
      shipmentTrackingNumber: isSet(object.shipmentTrackingNumber) ? String(object.shipmentTrackingNumber) : "",
      waitReturnRemindTimeout: isSet(object.waitReturnRemindTimeout)
        ? fromJsonTimestamp(object.waitReturnRemindTimeout)
        : undefined,
      canUpdateReturnItems: isSet(object.canUpdateReturnItems) ? Boolean(object.canUpdateReturnItems) : false,
      collectedTime: isSet(object.collectedTime) ? fromJsonTimestamp(object.collectedTime) : undefined,
      scannedTime: isSet(object.scannedTime) ? fromJsonTimestamp(object.scannedTime) : undefined,
      refundedTime: isSet(object.refundedTime) ? fromJsonTimestamp(object.refundedTime) : undefined,
      refundChannel: isSet(object.refundChannel) ? salesReturn_RefundChannelFromJSON(object.refundChannel) : 0,
      indicator: isSet(object.indicator) ? SalesReturn_Indicator.fromJSON(object.indicator) : undefined,
    };
  },

  toJSON(message: SalesReturn): unknown {
    const obj: any = {};
    message.id !== undefined && (obj.id = message.id);
    message.orderId !== undefined && (obj.orderId = message.orderId);
    message.state !== undefined && (obj.state = salesReturn_FrStateToJSON(message.state));
    message.createTime !== undefined && (obj.createTime = message.createTime.toISOString());
    message.paymentType !== undefined && (obj.paymentType = salesOrder_PaymentTypeToJSON(message.paymentType));
    if (message.items) {
      obj.items = message.items.map((e) => e ? SalesReturn_Item.toJSON(e) : undefined);
    } else {
      obj.items = [];
    }
    message.totalQuantity !== undefined && (obj.totalQuantity = Math.round(message.totalQuantity));
    message.total !== undefined && (obj.total = message.total);
    message.shipmentFee !== undefined && (obj.shipmentFee = message.shipmentFee);
    message.compensationAmount !== undefined && (obj.compensationAmount = message.compensationAmount);
    message.refundAmount !== undefined && (obj.refundAmount = message.refundAmount);
    message.returnRemindTimeout !== undefined && (obj.returnRemindTimeout = message.returnRemindTimeout.toISOString());
    message.returnChannel !== undefined && (obj.returnChannel = salesReturn_ReturnChannelToJSON(message.returnChannel));
    message.shipmentTrackingNumber !== undefined && (obj.shipmentTrackingNumber = message.shipmentTrackingNumber);
    message.waitReturnRemindTimeout !== undefined &&
      (obj.waitReturnRemindTimeout = message.waitReturnRemindTimeout.toISOString());
    message.canUpdateReturnItems !== undefined && (obj.canUpdateReturnItems = message.canUpdateReturnItems);
    message.collectedTime !== undefined && (obj.collectedTime = message.collectedTime.toISOString());
    message.scannedTime !== undefined && (obj.scannedTime = message.scannedTime.toISOString());
    message.refundedTime !== undefined && (obj.refundedTime = message.refundedTime.toISOString());
    message.refundChannel !== undefined && (obj.refundChannel = salesReturn_RefundChannelToJSON(message.refundChannel));
    message.indicator !== undefined &&
      (obj.indicator = message.indicator ? SalesReturn_Indicator.toJSON(message.indicator) : undefined);
    return obj;
  },

  create<I extends Exact<DeepPartial<SalesReturn>, I>>(base?: I): SalesReturn {
    return SalesReturn.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<SalesReturn>, I>>(object: I): SalesReturn {
    const message = createBaseSalesReturn();
    message.id = object.id ?? "";
    message.orderId = object.orderId ?? "";
    message.state = object.state ?? 0;
    message.createTime = object.createTime ?? undefined;
    message.paymentType = object.paymentType ?? 0;
    message.items = object.items?.map((e) => SalesReturn_Item.fromPartial(e)) || [];
    message.totalQuantity = object.totalQuantity ?? 0;
    message.total = object.total ?? "";
    message.shipmentFee = object.shipmentFee ?? "";
    message.compensationAmount = object.compensationAmount ?? "";
    message.refundAmount = object.refundAmount ?? "";
    message.returnRemindTimeout = object.returnRemindTimeout ?? undefined;
    message.returnChannel = object.returnChannel ?? 0;
    message.shipmentTrackingNumber = object.shipmentTrackingNumber ?? "";
    message.waitReturnRemindTimeout = object.waitReturnRemindTimeout ?? undefined;
    message.canUpdateReturnItems = object.canUpdateReturnItems ?? false;
    message.collectedTime = object.collectedTime ?? undefined;
    message.scannedTime = object.scannedTime ?? undefined;
    message.refundedTime = object.refundedTime ?? undefined;
    message.refundChannel = object.refundChannel ?? 0;
    message.indicator = (object.indicator !== undefined && object.indicator !== null)
      ? SalesReturn_Indicator.fromPartial(object.indicator)
      : undefined;
    return message;
  },
};

function createBaseSalesReturn_Item(): SalesReturn_Item {
  return {
    orderItemId: "",
    skuId: "",
    productName: "",
    productImage: "",
    colorName: "",
    sizeName: "",
    sizeSuffix: "",
    unitPrice: "",
    discountedUnitPrice: "",
    promotionName: "",
    quantity: 0,
    subtotal: "",
    reason: "",
    note: "",
  };
}

export const SalesReturn_Item = {
  fromJSON(object: any): SalesReturn_Item {
    return {
      orderItemId: isSet(object.orderItemId) ? String(object.orderItemId) : "",
      skuId: isSet(object.skuId) ? String(object.skuId) : "",
      productName: isSet(object.productName) ? String(object.productName) : "",
      productImage: isSet(object.productImage) ? String(object.productImage) : "",
      colorName: isSet(object.colorName) ? String(object.colorName) : "",
      sizeName: isSet(object.sizeName) ? String(object.sizeName) : "",
      sizeSuffix: isSet(object.sizeSuffix) ? String(object.sizeSuffix) : "",
      unitPrice: isSet(object.unitPrice) ? String(object.unitPrice) : "",
      discountedUnitPrice: isSet(object.discountedUnitPrice) ? String(object.discountedUnitPrice) : "",
      promotionName: isSet(object.promotionName) ? String(object.promotionName) : "",
      quantity: isSet(object.quantity) ? Number(object.quantity) : 0,
      subtotal: isSet(object.subtotal) ? String(object.subtotal) : "",
      reason: isSet(object.reason) ? String(object.reason) : "",
      note: isSet(object.note) ? String(object.note) : "",
    };
  },

  toJSON(message: SalesReturn_Item): unknown {
    const obj: any = {};
    message.orderItemId !== undefined && (obj.orderItemId = message.orderItemId);
    message.skuId !== undefined && (obj.skuId = message.skuId);
    message.productName !== undefined && (obj.productName = message.productName);
    message.productImage !== undefined && (obj.productImage = message.productImage);
    message.colorName !== undefined && (obj.colorName = message.colorName);
    message.sizeName !== undefined && (obj.sizeName = message.sizeName);
    message.sizeSuffix !== undefined && (obj.sizeSuffix = message.sizeSuffix);
    message.unitPrice !== undefined && (obj.unitPrice = message.unitPrice);
    message.discountedUnitPrice !== undefined && (obj.discountedUnitPrice = message.discountedUnitPrice);
    message.promotionName !== undefined && (obj.promotionName = message.promotionName);
    message.quantity !== undefined && (obj.quantity = Math.round(message.quantity));
    message.subtotal !== undefined && (obj.subtotal = message.subtotal);
    message.reason !== undefined && (obj.reason = message.reason);
    message.note !== undefined && (obj.note = message.note);
    return obj;
  },

  create<I extends Exact<DeepPartial<SalesReturn_Item>, I>>(base?: I): SalesReturn_Item {
    return SalesReturn_Item.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<SalesReturn_Item>, I>>(object: I): SalesReturn_Item {
    const message = createBaseSalesReturn_Item();
    message.orderItemId = object.orderItemId ?? "";
    message.skuId = object.skuId ?? "";
    message.productName = object.productName ?? "";
    message.productImage = object.productImage ?? "";
    message.colorName = object.colorName ?? "";
    message.sizeName = object.sizeName ?? "";
    message.sizeSuffix = object.sizeSuffix ?? "";
    message.unitPrice = object.unitPrice ?? "";
    message.discountedUnitPrice = object.discountedUnitPrice ?? "";
    message.promotionName = object.promotionName ?? "";
    message.quantity = object.quantity ?? 0;
    message.subtotal = object.subtotal ?? "";
    message.reason = object.reason ?? "";
    message.note = object.note ?? "";
    return message;
  },
};

function createBaseSalesReturn_Indicator(): SalesReturn_Indicator {
  return { new: undefined, waitConfirm: undefined, processing: undefined, success: undefined };
}

export const SalesReturn_Indicator = {
  fromJSON(object: any): SalesReturn_Indicator {
    return {
      new: isSet(object.new) ? SalesReturn_Indicator_Item.fromJSON(object.new) : undefined,
      waitConfirm: isSet(object.waitConfirm) ? SalesReturn_Indicator_Item.fromJSON(object.waitConfirm) : undefined,
      processing: isSet(object.processing) ? SalesReturn_Indicator_Item.fromJSON(object.processing) : undefined,
      success: isSet(object.success) ? SalesReturn_Indicator_Item.fromJSON(object.success) : undefined,
    };
  },

  toJSON(message: SalesReturn_Indicator): unknown {
    const obj: any = {};
    message.new !== undefined && (obj.new = message.new ? SalesReturn_Indicator_Item.toJSON(message.new) : undefined);
    message.waitConfirm !== undefined &&
      (obj.waitConfirm = message.waitConfirm ? SalesReturn_Indicator_Item.toJSON(message.waitConfirm) : undefined);
    message.processing !== undefined &&
      (obj.processing = message.processing ? SalesReturn_Indicator_Item.toJSON(message.processing) : undefined);
    message.success !== undefined &&
      (obj.success = message.success ? SalesReturn_Indicator_Item.toJSON(message.success) : undefined);
    return obj;
  },

  create<I extends Exact<DeepPartial<SalesReturn_Indicator>, I>>(base?: I): SalesReturn_Indicator {
    return SalesReturn_Indicator.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<SalesReturn_Indicator>, I>>(object: I): SalesReturn_Indicator {
    const message = createBaseSalesReturn_Indicator();
    message.new = (object.new !== undefined && object.new !== null)
      ? SalesReturn_Indicator_Item.fromPartial(object.new)
      : undefined;
    message.waitConfirm = (object.waitConfirm !== undefined && object.waitConfirm !== null)
      ? SalesReturn_Indicator_Item.fromPartial(object.waitConfirm)
      : undefined;
    message.processing = (object.processing !== undefined && object.processing !== null)
      ? SalesReturn_Indicator_Item.fromPartial(object.processing)
      : undefined;
    message.success = (object.success !== undefined && object.success !== null)
      ? SalesReturn_Indicator_Item.fromPartial(object.success)
      : undefined;
    return message;
  },
};

function createBaseSalesReturn_Indicator_Item(): SalesReturn_Indicator_Item {
  return { title: "", desc: "", timestamp: undefined, enable: false };
}

export const SalesReturn_Indicator_Item = {
  fromJSON(object: any): SalesReturn_Indicator_Item {
    return {
      title: isSet(object.title) ? String(object.title) : "",
      desc: isSet(object.desc) ? String(object.desc) : "",
      timestamp: isSet(object.timestamp) ? fromJsonTimestamp(object.timestamp) : undefined,
      enable: isSet(object.enable) ? Boolean(object.enable) : false,
    };
  },

  toJSON(message: SalesReturn_Indicator_Item): unknown {
    const obj: any = {};
    message.title !== undefined && (obj.title = message.title);
    message.desc !== undefined && (obj.desc = message.desc);
    message.timestamp !== undefined && (obj.timestamp = message.timestamp.toISOString());
    message.enable !== undefined && (obj.enable = message.enable);
    return obj;
  },

  create<I extends Exact<DeepPartial<SalesReturn_Indicator_Item>, I>>(base?: I): SalesReturn_Indicator_Item {
    return SalesReturn_Indicator_Item.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<SalesReturn_Indicator_Item>, I>>(object: I): SalesReturn_Indicator_Item {
    const message = createBaseSalesReturn_Indicator_Item();
    message.title = object.title ?? "";
    message.desc = object.desc ?? "";
    message.timestamp = object.timestamp ?? undefined;
    message.enable = object.enable ?? false;
    return message;
  },
};

declare var self: any | undefined;
declare var window: any | undefined;
declare var global: any | undefined;
var tsProtoGlobalThis: any = (() => {
  if (typeof globalThis !== "undefined") {
    return globalThis;
  }
  if (typeof self !== "undefined") {
    return self;
  }
  if (typeof window !== "undefined") {
    return window;
  }
  if (typeof global !== "undefined") {
    return global;
  }
  throw "Unable to locate global object";
})();

type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;

export type DeepPartial<T> = T extends Builtin ? T
  : T extends Array<infer U> ? Array<DeepPartial<U>> : T extends ReadonlyArray<infer U> ? ReadonlyArray<DeepPartial<U>>
  : T extends {} ? { [K in keyof T]?: DeepPartial<T[K]> }
  : Partial<T>;

type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin ? P
  : P & { [K in keyof P]: Exact<P[K], I[K]> } & { [K in Exclude<keyof I, KeysOfUnion<P>>]: never };

function fromTimestamp(t: Timestamp): Date {
  let millis = (t.seconds || 0) * 1_000;
  millis += (t.nanos || 0) / 1_000_000;
  return new Date(millis);
}

function fromJsonTimestamp(o: any): Date {
  if (o instanceof Date) {
    return o;
  } else if (typeof o === "string") {
    return new Date(o);
  } else {
    return fromTimestamp(Timestamp.fromJSON(o));
  }
}

function isSet(value: any): boolean {
  return value !== null && value !== undefined;
}
